import { useState, useRef, useEffect, useCallback } from 'react';

type LangCode = 'ar-PS' | 'he-IL' | 'en-US';

type UseSpeechToTextOptions = {
  lang?: LangCode;
};

type UseSpeechToTextReturn = {
  start: () => void;
  stop: () => void;
  reset: () => void;
  listening: boolean;
  transcript: string;
  interim: string;
  error: string | null;
  supported: boolean;
  lang: LangCode;
  setLang: (lang: LangCode) => void;
};

const getSpeechRecognitionConstructor = (): any | null => {
  if (typeof window === 'undefined') return null;
  const AnyWindow = window as any;
  return AnyWindow.SpeechRecognition || AnyWindow.webkitSpeechRecognition || null;
};

export function useSpeechToText(options: UseSpeechToTextOptions = {}): UseSpeechToTextReturn {
  const RecognitionConstructor = getSpeechRecognitionConstructor();
  const [supported] = useState(() => Boolean(RecognitionConstructor));
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lang, setLang] = useState<LangCode>(options.lang ?? 'ar-PS');

  const recognitionRef = useRef<any | null>(null);
  const silenceTimerRef = useRef<number | undefined>(undefined);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = undefined;
    }
  }, []);

  const scheduleSilenceStop = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = window.setTimeout(() => {
      recognitionRef.current?.stop();
    }, 5000);
  }, [clearSilenceTimer]);

  const reset = useCallback(() => {
    setTranscript('');
    setInterim('');
    setError(null);
  }, []);

  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = lang;
    }
  }, [lang]);

  useEffect(() => () => clearSilenceTimer(), [clearSilenceTimer]);

  const stop = useCallback(() => {
    if (recognitionRef.current && listening) {
      try {
        recognitionRef.current.stop();
      } catch (err) {
        // ignore stop errors
      }
    }
    clearSilenceTimer();
  }, [listening, clearSilenceTimer]);

  const start = useCallback(() => {
    if (!supported || !RecognitionConstructor) {
      setError('Microphone permission required');
      return;
    }

    if (listening) {
      return;
    }

    try {
      if (!recognitionRef.current) {
        recognitionRef.current = new RecognitionConstructor();
      }
    } catch (err) {
      setError('Microphone permission required');
      return;
    }

    const recognition = recognitionRef.current;
    recognition.lang = lang;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    setTranscript('');
    setInterim('');
    setError(null);

    recognition.onstart = () => {
      setListening(true);
      scheduleSilenceStop();
    };

    recognition.onresult = (event: any) => {
      let finalText = '';
      let interimText = '';

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result[0]?.transcript ?? '';
        if (result.isFinal) {
          finalText += text;
        } else {
          interimText += text;
        }
      }

      if (finalText.trim()) {
        setTranscript((prev) => (prev ? `${prev} ${finalText.trim()}` : finalText.trim()));
      }

      setInterim(interimText.trim());
      scheduleSilenceStop();
    };

    recognition.onerror = (event: any) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setError('Microphone permission required');
      } else if (event.error) {
        setError(String(event.error));
      } else {
        setError('Speech recognition error');
      }
      setListening(false);
      clearSilenceTimer();
    };

    recognition.onend = () => {
      setListening(false);
      clearSilenceTimer();
      setInterim('');
    };

    recognition.onsoundend = () => {
      scheduleSilenceStop();
    };

    try {
      recognition.start();
    } catch (err) {
      setError('Microphone permission required');
    }
  }, [RecognitionConstructor, lang, scheduleSilenceStop, clearSilenceTimer, supported, listening]);

  return {
    start,
    stop,
    reset,
    listening,
    transcript,
    interim,
    error,
    supported,
    lang,
    setLang,
  };
}

export type { LangCode, UseSpeechToTextReturn };
